import { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Upload, FileText, Loader2, CheckCircle2, AlertTriangle,
  Database, Users, Calendar, ChevronRight, ChevronLeft, Copy, History,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;

const SOURCES = [
  { key: "total_drive", label: "Total Drive", color: "bg-blue-100 text-blue-700" },
  { key: "idriving", label: "iDriving", color: "bg-purple-100 text-purple-700" },
  { key: "mydrivetime", label: "MyDriveTime", color: "bg-emerald-100 text-emerald-700" },
  { key: "generic", label: "Generic CSV", color: "bg-slate-100 text-slate-700" },
];

const TYPES = [
  { key: "pupils", label: "Pupils", icon: Users, description: "Contact details, balances, notes" },
  { key: "bookings", label: "Bookings / Lessons", icon: Calendar, description: "Scheduled lessons, times, prices" },
];

export default function MigrationTools() {
  const nav = useNavigate();
  const [step, setStep] = useState(1); // 1=choose, 2=upload, 3=map, 4=confirm, 5=done
  const [source, setSource] = useState("");
  const [type, setType] = useState("pupils");
  const [csvText, setCsvText] = useState("");
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [jobs, setJobs] = useState([]);
  const fileRef = useRef(null);

  const loadJobs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/migration/jobs`, { withCredentials: true });
      setJobs(data.jobs || []);
    } catch { /* noop */ }
  }, []);
  useEffect(() => { loadJobs(); }, [loadJobs]);

  const onFile = async (f) => {
    if (!f) return;
    const text = await f.text();
    setCsvText(text);
  };

  const parse = async () => {
    if (!csvText.trim()) { toast.error("Paste or upload a CSV first"); return; }
    setParsing(true);
    try {
      const { data } = await axios.post(`${API}/api/migration/parse`,
        { csv_text: csvText, type }, { withCredentials: true });
      setParsed(data);
      setMapping(data.suggested_mapping || {});
      setStep(3);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not parse file");
    }
    setParsing(false);
  };

  const runImport = async (dryRun = false) => {
    setImporting(true);
    try {
      const { data } = await axios.post(`${API}/api/migration/import`,
        { type, source: source || "generic", csv_text: csvText, mapping, dry_run: dryRun },
        { withCredentials: true });
      setResult(data);
      if (!dryRun) {
        setStep(5);
        await loadJobs();
      } else {
        setStep(4);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    }
    setImporting(false);
  };

  const reset = () => {
    setStep(1); setCsvText(""); setParsed(null); setMapping({}); setResult(null); setSource(""); setType("pupils");
  };

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24">
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-3">
          <button data-testid="mig-back" onClick={() => step === 1 ? nav("/instructor") : setStep(s => s - 1)}
            className="p-2 -ml-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700">
            <ArrowLeft className="w-4 h-4 text-slate-600 dark:text-slate-400" />
          </button>
          <div className="flex-1">
            <p className="text-sm font-extrabold text-slate-900 dark:text-white">Migration Tools</p>
            <p className="text-[10px] text-slate-500 dark:text-slate-400">Step {step} of 5</p>
          </div>
        </div>
        <div className="h-1 bg-slate-100 dark:bg-slate-700">
          <div className="h-1 bg-blue-600 transition-all" style={{ width: `${(step / 5) * 100}%` }} />
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {step === 1 && <StepChoose source={source} setSource={setSource} type={type} setType={setType}
                                   onNext={() => setStep(2)} jobs={jobs} />}
        {step === 2 && <StepUpload source={source} type={type} csvText={csvText} setCsvText={setCsvText}
                                   onFile={onFile} fileRef={fileRef} onParse={parse} parsing={parsing} />}
        {step === 3 && <StepMap parsed={parsed} mapping={mapping} setMapping={setMapping}
                                onPreview={() => runImport(true)} importing={importing} />}
        {step === 4 && <StepConfirm result={result} onConfirm={() => runImport(false)}
                                     onBack={() => setStep(3)} importing={importing} />}
        {step === 5 && <StepDone result={result} onAnother={reset} onDashboard={() => nav("/instructor")} />}
      </div>
    </div>
  );
}

/* ─── Step 1: Choose source + type ─── */
function StepChoose({ source, setSource, type, setType, onNext, jobs }) {
  return (
    <div className="space-y-6">
      <Card title="What are you migrating from?" subtitle="We'll auto-detect column names for common platforms.">
        <div className="grid grid-cols-2 gap-2">
          {SOURCES.map(s => (
            <button key={s.key} data-testid={`mig-src-${s.key}`} onClick={() => setSource(s.key)}
              className={`h-14 rounded-xl text-sm font-bold border transition ${source === s.key
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-600 hover:border-blue-300"}`}>
              {s.label}
            </button>
          ))}
        </div>
      </Card>

      <Card title="What would you like to import?" subtitle="You can run this multiple times.">
        <div className="space-y-2">
          {TYPES.map(t => (
            <button key={t.key} data-testid={`mig-type-${t.key}`} onClick={() => setType(t.key)}
              className={`w-full p-3 rounded-xl border text-left flex items-center gap-3 transition ${type === t.key
                ? "bg-blue-50 dark:bg-blue-900/30 border-blue-500"
                : "bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600 hover:border-blue-300"}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${type === t.key ? "bg-blue-600" : "bg-slate-100 dark:bg-slate-600"}`}>
                <t.icon className={`w-5 h-5 ${type === t.key ? "text-white" : "text-slate-600 dark:text-slate-300"}`} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold text-slate-900 dark:text-white">{t.label}</p>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">{t.description}</p>
              </div>
              {type === t.key && <CheckCircle2 className="w-5 h-5 text-blue-600" />}
            </button>
          ))}
        </div>
      </Card>

      <Button data-testid="mig-continue" onClick={onNext} disabled={!source}
        className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl">
        Continue <ChevronRight className="w-4 h-4 ml-2" />
      </Button>

      {jobs.length > 0 && (
        <Card title="Previous imports" icon={History}>
          <div className="space-y-2">
            {jobs.slice(0, 5).map(j => (
              <div key={j.id} data-testid={`mig-job-${j.id}`} className="flex items-center justify-between text-xs bg-slate-50 dark:bg-slate-900 rounded-lg px-3 py-2">
                <div>
                  <p className="font-bold text-slate-900 dark:text-white">{j.type} · {j.source}</p>
                  <p className="text-slate-500 dark:text-slate-400 text-[10px]">{new Date(j.created_at).toLocaleString("en-GB")}</p>
                </div>
                <div className="text-right">
                  <p className="text-emerald-600 font-bold">+{j.created || 0}</p>
                  <p className="text-slate-400 text-[10px]">{j.updated || 0} updated · {j.skipped || 0} skipped</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

/* ─── Step 2: Upload or paste ─── */
function StepUpload({ source, type, csvText, setCsvText, onFile, fileRef, onParse, parsing }) {
  return (
    <div className="space-y-4">
      <Card title="Upload your export" subtitle={`${source.replace("_", " ")} · ${type}`} icon={Upload}>
        <input ref={fileRef} type="file" accept=".csv,.tsv,.txt" data-testid="mig-file"
          onChange={e => onFile(e.target.files?.[0])} className="hidden" />
        <button onClick={() => fileRef.current?.click()} data-testid="mig-file-btn"
          className="w-full h-20 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl flex flex-col items-center justify-center text-slate-500 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition">
          <Upload className="w-5 h-5 mb-1" />
          <span className="text-sm font-semibold">Choose a CSV file</span>
        </button>
      </Card>

      <Card title="Or paste directly" subtitle="Copy-paste CSV or tab-separated data" icon={Copy}>
        <Textarea data-testid="mig-paste" value={csvText} onChange={e => setCsvText(e.target.value)}
          rows={8} placeholder="Name,Email,Phone,Postcode&#10;John Smith,john@example.com,07123456789,SW1A 1AA&#10;Jane Doe,jane@example.com,07987654321,E1 6AN"
          className="font-mono text-xs" />
        <p className="text-[10px] text-slate-400 mt-2">
          {csvText ? `${csvText.split("\n").length - 1} row${csvText.split("\n").length - 1 === 1 ? "" : "s"} detected` : "Include a header row with column names"}
        </p>
      </Card>

      <Button data-testid="mig-parse" onClick={onParse} disabled={!csvText.trim() || parsing}
        className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl">
        {parsing ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Parse file <ChevronRight className="w-4 h-4 ml-2" /></>}
      </Button>
    </div>
  );
}

/* ─── Step 3: Field mapping ─── */
function StepMap({ parsed, mapping, setMapping, onPreview, importing }) {
  if (!parsed) return null;
  const fields = parsed.available_fields || [];
  const setField = (field, header) => setMapping(m => ({ ...m, [field]: header || undefined }));

  return (
    <div className="space-y-4">
      <Card title="Map your columns" subtitle={`${parsed.row_count} rows found — auto-matched where possible.`} icon={Database}>
        <div className="space-y-2.5">
          {fields.map(f => (
            <div key={f} className="flex items-center gap-2">
              <Label className="flex-shrink-0 w-36 text-xs capitalize">{f.replace(/_/g, " ")}</Label>
              <select data-testid={`mig-map-${f}`} value={mapping[f] || ""}
                onChange={e => setField(f, e.target.value)}
                className="flex-1 h-9 px-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-xs">
                <option value="">— skip —</option>
                {parsed.headers.map(h => <option key={h} value={h}>{h}</option>)}
              </select>
            </div>
          ))}
        </div>
      </Card>

      {parsed.sample_rows?.length > 0 && (
        <Card title="Sample rows" subtitle="First 5 rows from your file" icon={FileText}>
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-[10px]">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  {parsed.headers.slice(0, 5).map(h => (
                    <th key={h} className="px-2 py-1.5 text-left font-bold text-slate-600 dark:text-slate-300">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {parsed.sample_rows.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 dark:border-slate-700/50">
                    {parsed.headers.slice(0, 5).map(h => (
                      <td key={h} className="px-2 py-1.5 text-slate-700 dark:text-slate-300 truncate max-w-[120px]">{r[h] || "—"}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Button data-testid="mig-preview" onClick={onPreview} disabled={importing}
        className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl">
        {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Preview import <ChevronRight className="w-4 h-4 ml-2" /></>}
      </Button>
    </div>
  );
}

/* ─── Step 4: Confirm (dry-run results) ─── */
function StepConfirm({ result, onConfirm, onBack, importing }) {
  if (!result) return null;
  return (
    <div className="space-y-4">
      <Card title="Ready to import" subtitle="Review the dry-run summary below, then confirm.">
        <div className="grid grid-cols-3 gap-2 text-center mb-4">
          <Stat label="To create" value={result.preview?.length || 0} color="text-emerald-600" />
          <Stat label="Skipped" value={result.skipped || 0} color="text-amber-600" />
          <Stat label="Errors" value={(result.errors || []).length} color="text-red-600" />
        </div>
        {result.errors?.length > 0 && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg p-3 mb-3">
            <p className="text-xs font-bold text-amber-800 dark:text-amber-200 flex items-center gap-1 mb-1">
              <AlertTriangle className="w-3.5 h-3.5" /> {result.errors.length} issue{result.errors.length === 1 ? "" : "s"}
            </p>
            <ul className="text-[10px] text-amber-800 dark:text-amber-300 space-y-0.5 max-h-20 overflow-y-auto">
              {result.errors.slice(0, 10).map((e, i) => (
                <li key={i}>Row {e.row}: {e.reason}</li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-2 gap-2">
        <Button data-testid="mig-back-map" onClick={onBack} variant="outline" className="h-12 rounded-xl">
          <ChevronLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <Button data-testid="mig-confirm" onClick={onConfirm} disabled={importing}
          className="h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl">
          {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Confirm import <CheckCircle2 className="w-4 h-4 ml-2" /></>}
        </Button>
      </div>
    </div>
  );
}

/* ─── Step 5: Done ─── */
function StepDone({ result, onAnother, onDashboard }) {
  const nav = useNavigate();
  if (!result) return null;
  return (
    <div className="space-y-4">
      <div data-testid="mig-done" className="bg-white dark:bg-slate-800 rounded-3xl border border-emerald-200 dark:border-emerald-900 p-6 text-center shadow-sm">
        <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" />
        </div>
        <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white mb-1">Migration complete</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">Your data is now live in your dashboard.</p>
        <div className="grid grid-cols-3 gap-2 text-center">
          <Stat label="Created" value={result.created || 0} color="text-emerald-600" />
          <Stat label="Updated" value={result.updated || 0} color="text-blue-600" />
          <Stat label="Skipped" value={result.skipped || 0} color="text-amber-600" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button data-testid="mig-another" onClick={onAnother} variant="outline" className="h-12 rounded-xl">
          Import more
        </Button>
        <Button data-testid="mig-go-dashboard" onClick={() => nav("/pupils")} className="h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl">
          View pupils
        </Button>
      </div>
      <Button data-testid="mig-dashboard" onClick={onDashboard} variant="ghost" className="w-full">
        Back to dashboard
      </Button>
    </div>
  );
}

/* ─── UI primitives ─── */
function Card({ title, subtitle, icon: Icon, children }) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 shadow-sm">
      <div className="flex items-start gap-3 mb-4">
        {Icon && <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 text-blue-600" />
        </div>}
        <div className="flex-1">
          <h2 className="text-base font-extrabold text-slate-900 dark:text-white">{title}</h2>
          {subtitle && <p className="text-xs text-slate-500 dark:text-slate-400">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="bg-slate-50 dark:bg-slate-900 rounded-lg py-3">
      <p className={`text-2xl font-extrabold ${color}`}>{value}</p>
      <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider">{label}</p>
    </div>
  );
}
